import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { SEO } from "@/components/seo/SEO";
import { Lock, Download, ShieldOff, AlertTriangle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import LifecycleSection from "@/components/landing/LifecycleSection";
import MarketingNav from "@/components/MarketingNav";
import { useAuth } from "@/contexts/AuthContext";
import { track } from "@/lib/analytics";
import heroMockup from "@/assets/landing-hero-transparent.webp";
import manifestLogo from "@/assets/manifest-logo.webp";
import featureCapture from "@/assets/feature-capture.webp";
import featureRecall from "@/assets/feature-recall.webp";
import featureValue from "@/assets/feature-value.webp";
import featureReturns from "@/assets/feature-returns.webp";
import featureOrganisation from "@/assets/feature-organisation.webp";
import featureInsurance from "@/assets/feature-insurance.webp";

// Feature cards in order: Capture, Returns, Safety, Organisation, Value, Insurance
const FEATURES = [
  {
    tag: "Capture",
    title: "Snap, forward, or just say it.",
    body: "Photo, receipt, email, or voice. Describe what you bought and AI extracts the brand, model, price, warranty, and return window. Scan up to 8 items at once or add in bulk. You don't even have to type.",
    image: featureCapture,
  },
  {
    tag: "Returns",
    title: "The drawer of doom, solved.",
    body: "We track return windows for the top retailers in every country we support. Best Buy's 15 days. Costco's 90. You get an alert before it's too late, not after.",
    image: featureReturns,
  },
  {
    tag: "Safety · Always free",
    tagColor: "#059669",
    title: "Recall alerts. Free. Always.",
    body: "Every day, we check your specific products against CPSC recall databases. Not your category — your actual stuff. Because safety shouldn't be a premium feature.",
    image: featureRecall,
  },
  {
    tag: "Organisation",
    title: "Where is the thing?",
    body: "Filter by room, category, status, or warranty. Everything in one place, organised however makes sense to you. Like a functioning adult.",
    image: featureOrganisation,
  },
  {
    tag: "Value",
    title: "What's it worth now?",
    body: "Not what you paid in 2022. What it's worth today. AI checks live marketplace listings using your item's age, brand, and model. Stop guessing.",
    image: featureValue,
  },
  {
    tag: "Insurance",
    title: "The PDF you don't have yet.",
    body: "One tap. Photos, serial numbers, barcodes, what you paid, what it's worth now (ACV), what it'd cost to replace (RCV), room by room. The insurance documentation every adjuster asks for and nobody ever has.",
    image: featureInsurance,
  },
];



const STEPS = [
  {
    number: "1",
    title: "Photo, receipt, or voice",
    body: "Take a photo, forward a receipt, or just describe it out loud. AI pulls the brand, model, serial number, price, and warranty. Zero typing required.",
  },
  {
    number: "2",
    title: "We deal with the filing",
    body: "Everything sorted, dated, and tracked. Return windows, warranties, and recall alerts run themselves. You continue not thinking about it.",
  },
  {
    number: "3",
    title: "It's there when you need it",
    body: "File a claim. Return something. Prove you own it. One tap, done. This is the part where past you did something nice for future you.",
  },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [vaultLoading, setVaultLoading] = useState(false);
  const [boostLoading, setBoostLoading] = useState(false);
  const [unlimitedLoading, setUnlimitedLoading] = useState(false);

  const handleCheckout = async (
    product: string,
    plan: string | undefined,
    setLoading: (v: boolean) => void
  ) => {
    track("checkout_initiated", { product, plan: plan ?? "default" });
    if (!user) {
      navigate(`/login?plan=${product}`);
      return;
    }
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const body: Record<string, string> = { product };
      if (plan) body.plan = plan;
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body,
      });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast({ title: "Checkout failed", description: "Something went wrong. Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#111]">
      <SEO
        title="Manifest – Home Inventory App for Insurance & Claims"
        description="Manifest is the home inventory app for real lives. AI photo scanning, receipt parsing, warranty and recall tracking, plus insurance-ready claim packets."
        canonical="https://manifestme.me"
        type="website"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Manifest",
            applicationCategory: "UtilitiesApplication",
            operatingSystem: "Web, iOS, Android",
            description: "Manifest helps homeowners document their belongings with AI-powered photo scanning and email receipt parsing. Generate insurance-ready claim packets instantly.",
            url: "https://manifestme.me",
            offers: {
              "@type": "Offer",
              price: "1.99",
              priceCurrency: "USD",
              priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: "1.99",
                priceCurrency: "USD",
                unitCode: "MON",
              },
            },
            image: "https://manifestme.me/og-image.png",
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What happens when my trial ends?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Your data is safe. You can upgrade to Vault or export everything — whichever works for you. Nothing gets deleted, nothing gets held hostage.",
                },
              },
              {
                "@type": "Question",
                name: "What's the difference between Vault and Unlimited?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Vault gives you a few AI actions per month — enough for occasional use. Unlimited removes all limits. If you're doing a big project or just don't want to think about it, Unlimited is the one.",
                },
              },
              {
                "@type": "Question",
                name: "Is Boost recurring?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No. 14 days, then it stops. Buy another one when you need it. If you buy two, they stack.",
                },
              },
              {
                "@type": "Question",
                name: "Can I use Boost on the free trial?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "The trial already includes full access for 7 days. There's nothing Boost adds that the trial doesn't cover — save it for later.",
                },
              },
            ],
          },
        ]}
      />
      {/* Navbar */}
      <MarketingNav linksToHome={false} />

      {/* Hero */}
      <section className="mx-auto max-w-[1200px] px-5 pt-[80px] pb-[60px] md:pt-[100px] md:pb-[80px]">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="font-editorial text-[clamp(2.6rem,4.5vw,3.8rem)] leading-[1.05] tracking-[-0.035em]">
              You own stuff.<br />
              <em>You should probably keep track of it.</em>
            </h1>
            <p className="mt-5 text-[1.05rem] text-[#374151] leading-[1.7] max-w-[480px]">
              Snap a photo, forward a receipt, or just say what you bought. Manifest builds your home inventory for you.
              Return deadlines, warranties, recalls, resale value, insurance records.
              This sounds like a pretty responsible thing to do, right?
            </p>
            <ul className="mt-6 space-y-2.5">
              {[
                "Forward a receipt, snap a photo, or describe it with your voice. We dig out the brand, model, serial number, UPC, price, warranty, and return window. You just point your phone — or talk to it.",
                "Remembers your return deadlines. Best Buy gives you 15 days. Nordstrom gives you a year. We know the difference so you don't have to.",
                "Makes a complete PDF for insurance claims. Get ready to impress a whole office of claims assessors.",
              ].map((text, i) => (
                <li key={i} className="flex items-start gap-3 text-[0.88rem] text-[#374151]">
                  <span className="mt-2 h-2 w-2 rounded-full bg-[#0F766E] shrink-0" />
                  {text}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex items-center gap-4 flex-wrap">
              <Link to="/login" className="rounded-[10px] bg-[#111] px-7 py-3 text-sm font-semibold text-white hover:bg-[#222] transition-colors">
                Start for free
              </Link>
              <a href="#how-it-works" className="text-sm text-[#6B7280] hover:text-[#374151] transition-colors">
                See how it works
              </a>
            </div>
            <p className="mt-4 text-[0.75rem] text-[#9CA3AF]">
              Free plan available · No credit card · No spreadsheet
            </p>
          </div>
          <div className="flex justify-center">
            <img
              src={heroMockup}
              alt="Manifest app showing inventory dashboard with recall alerts, warranty tracking, and asset management"
              className="w-full max-w-[480px] h-auto"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              width={480}
              height={480}
            />
          </div>
        </div>
      </section>

      {/* Purchase Lifecycle */}
      <LifecycleSection />

      {/* How it works */}
      <section id="how-it-works" className="border-b border-[#E5E5E0] bg-[#FAFAF7]">
        <div className="mx-auto max-w-[1200px] px-5 py-20">
          <p className="text-[12px] font-medium text-[#6B7280] uppercase tracking-[0.08em] mb-2">How it works</p>
          <h2 className="font-editorial text-[clamp(1.8rem,3vw,2.4rem)] leading-[1.1] mb-4">
            It's actually fine. We promise.
          </h2>
          <p className="text-[13px] text-[#6B7280] mb-12 max-w-[500px]">
            Three steps. The hardest part is pointing your phone at something.
          </p>
          <div className="relative">
            {/* Timeline line - desktop */}
            <div className="hidden md:block absolute top-5 left-[24px] right-[24px] h-px bg-[#E5E5E0]" />
            {/* Timeline line - mobile */}
            <div className="md:hidden absolute top-0 bottom-0 left-5 w-px bg-[#E5E5E0]" />

            <div className="grid md:grid-cols-3 gap-8 md:gap-12">
              {STEPS.map((step, i) => (
                <div key={i} className="relative flex md:flex-col gap-4 md:gap-0">
                  <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-[#E5E5E0] bg-white md:mb-6 hover:border-[#0F766E] hover:text-[#0F766E] transition-colors group">
                    <span className="font-editorial text-sm text-[#111] group-hover:text-[#0F766E]">{step.number}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[14px] text-[#111] mb-1.5">{step.title}</h3>
                    <p className="text-[13px] text-[#6B7280] leading-relaxed">{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-[1200px] px-5 py-20">
        <p className="text-[12px] font-medium text-[#6B7280] uppercase tracking-[0.08em] mb-2">Features</p>
        <h2 className="font-editorial text-[clamp(1.8rem,3vw,2.4rem)] leading-[1.1] mb-2">
          Things Manifest does so you don't have to
        </h2>
        <p className="text-[13px] text-[#6B7280] mb-12">None of these are exciting. At least one of them will ruin a Sunday afternoon.</p>

        <div className="grid md:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => (
            <div
              key={i}
              className="rounded-[14px] border border-[#E5E5E0] bg-white overflow-hidden hover:border-[#ccc] hover:shadow-sm transition-all flex flex-col"
            >
              <div className="p-6 pb-4">
                <span
                  className="text-[11px] font-bold uppercase tracking-[0.06em]"
                  style={{ color: f.tagColor || "#0F766E" }}
                >
                  {f.tag}
                </span>
                <h3 className="font-editorial text-[1.15rem] tracking-tight mt-2 mb-2">{f.title}</h3>
                <p className="text-[13px] text-[#6B7280] leading-[1.6]">{f.body}</p>
              </div>
              <div className="mt-auto px-4 pb-4">
                <div className="rounded-xl overflow-hidden border border-[#E5E5E0] bg-[#F7F5F0]">
                  <img
                    src={f.image}
                    alt={f.title}
                    className="w-full h-[180px] object-contain object-center p-2"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-y border-[#E5E5E0] bg-white">
        <div className="mx-auto max-w-[1200px] px-5 py-20">
          <p className="text-[12px] font-medium text-[#6B7280] uppercase tracking-[0.08em] mb-2">Pricing</p>
          <h2 className="font-editorial text-[clamp(1.8rem,3vw,2.4rem)] leading-[1.1] mb-2">
            Simple pricing. On purpose.
          </h2>
          <p className="text-[13px] text-[#6B7280] mb-1.5 max-w-[520px]">
            Manifest isn't a daily-use app. You set it up, it runs quietly in the background, and you come back when
            something actually happens. The pricing is built around that — not around keeping you subscribed forever.
          </p>
          <p className="text-[12px] text-[#9CA3AF] mb-10 max-w-[520px]">
            Moving house? Filing a claim? Just bought a lot of stuff all at once? There's a plan for that too.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">

            {/* Free Trial */}
            <div className="rounded-[14px] border border-[#E5E5E0] bg-[#FAFAF7] p-6 flex flex-col">
              <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#6B7280]">Free Trial</span>
              <div className="mt-3 mb-2">
                <span className="font-editorial text-[2rem] tracking-tight">7 days</span>
              </div>
              <p className="text-[12px] text-[#6B7280] mb-1 leading-relaxed">
                Full access for a week. No card required, no tricks. Just try it.
              </p>
              <p className="text-[11px] text-[#9CA3AF] mb-5 leading-relaxed">
                When the trial ends, your data is safe. Upgrade or export — your call.
              </p>
              <ul className="space-y-2.5 text-[13px] text-[#374151] flex-1">
                {[
                  "7 days, full access",
                  "All features unlocked",
                  "No credit card",
                  "No commitment",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#9CA3AF] shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/login" className="mt-6 block w-full text-center rounded-[10px] border border-[#E5E5E0] bg-white px-5 py-2.5 text-sm font-semibold text-[#111] hover:bg-[#F0EFEB] transition-colors">
                Start free trial
              </Link>
            </div>

            {/* Vault */}
            <div className="rounded-[14px] border border-[#0F766E] bg-white p-6 flex flex-col relative">
              <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#0F766E]">Vault</span>
              <div className="mt-3 mb-1">
                <span className="font-editorial text-[2rem] tracking-tight">$11.99</span>
                <span className="text-[13px] text-[#6B7280] ml-1">/year</span>
              </div>
              <p className="text-[12px] text-[#0F766E] font-medium mb-1">Or $1.99/month</p>
              <p className="text-[12px] text-[#9CA3AF] mb-5 leading-relaxed">
                Keeps your inventory, receipts, and records in one place. Set it up, leave it running. Light AI allowance for when you need it.
              </p>
              <ul className="space-y-2.5 text-[13px] text-[#374151] flex-1">
                {[
                  "Unlimited assets",
                  "Light AI allowance (~15 scans/month)",
                  "Search and exports",
                  "Recall alerts",
                  "Return window tracking",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#0F766E] shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                disabled={vaultLoading}
                onClick={() => handleCheckout("vault", "annual", setVaultLoading)}
                className="mt-6 flex items-center justify-center gap-2 w-full text-center rounded-[10px] border border-[#0F766E] bg-white px-5 py-2.5 text-sm font-semibold text-[#0F766E] hover:bg-[#F0FDF4] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {vaultLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Get Vault
              </button>
            </div>

            {/* Boost */}
            <div className="rounded-[14px] border border-[#E5E5E0] bg-white p-6 flex flex-col">
              <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#6B7280]">Boost</span>
              <div className="mt-3 mb-1">
                <span className="font-editorial text-[2rem] tracking-tight">$3.99</span>
                <span className="text-[13px] text-[#6B7280] ml-1">/ 14 days</span>
              </div>
              <p className="text-[12px] text-[#6B7280] font-medium mb-1">One-time. Not a subscription.</p>
              <p className="text-[12px] text-[#9CA3AF] mb-5 leading-relaxed">
                A burst of expanded AI help for when you actually need it. 14 days, then it stops. Buy another whenever. Stacks if you do.
              </p>
              <ul className="space-y-2.5 text-[13px] text-[#374151] flex-1">
                {[
                  "Receipt scanning",
                  "Bulk capture",
                  "AI categorization",
                  "Listing generation",
                  "Claim and export assistance",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#9CA3AF] shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                disabled={boostLoading}
                onClick={() => handleCheckout("boost", undefined, setBoostLoading)}
                className="mt-6 flex items-center justify-center gap-2 w-full text-center rounded-[10px] border border-[#E5E5E0] bg-white px-5 py-2.5 text-sm font-semibold text-[#111] hover:bg-[#F0EFEB] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {boostLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Activate Boost
              </button>
            </div>

            {/* Unlimited */}
            <div className="rounded-[14px] border-2 border-[#111] bg-[#111] p-6 flex flex-col relative">
              <div className="absolute -top-3 left-6 bg-white text-[#111] text-[11px] font-bold uppercase tracking-[0.06em] px-3 py-1 rounded-full">
                Everything on
              </div>
              <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-white/60">Unlimited</span>
              <div className="mt-3 mb-1">
                <span className="font-editorial text-[2rem] tracking-tight text-white">$39</span>
                <span className="text-[13px] text-white/50 ml-1">/year</span>
              </div>
              <p className="text-[12px] text-white/50 mb-5 leading-relaxed">
                All AI features, no limits, no thinking about it. For people who use the app regularly and don't want to manage boosts.
              </p>
              <ul className="space-y-2.5 text-[13px] text-white/80 flex-1">
                {[
                  "Everything in Vault",
                  "All AI features",
                  "No monthly limits",
                  "No managing boosts",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-white/40 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                disabled={unlimitedLoading}
                onClick={() => handleCheckout("unlimited", "annual", setUnlimitedLoading)}
                className="mt-6 flex items-center justify-center gap-2 w-full text-center rounded-[10px] bg-white px-5 py-2.5 text-sm font-semibold text-[#111] hover:bg-white/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {unlimitedLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Go Unlimited
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* Pricing FAQ */}
      <section className="border-b border-[#E5E5E0] bg-[#FAFAF7]">
        <div className="mx-auto max-w-[800px] px-5 py-16">
          <p className="text-[12px] font-medium text-[#6B7280] uppercase tracking-[0.08em] mb-6">Common questions</p>
          <div className="space-y-0 divide-y divide-[#E5E5E0]">
            {[
              {
                q: "What happens when my trial ends?",
                a: "Your data is safe. You can upgrade to Vault or export everything — whichever works for you. Nothing gets deleted, nothing gets held hostage.",
              },
              {
                q: "What's the difference between Vault and Unlimited?",
                a: "Vault gives you a few AI actions per month — enough for occasional use. Unlimited removes all limits. If you're doing a big project or just don't want to think about it, Unlimited is the one.",
              },
              {
                q: "Is Boost recurring?",
                a: "No. 14 days, then it stops. Buy another one when you need it. If you buy two, they stack.",
              },
              {
                q: "Can I use Boost on the free trial?",
                a: "The trial already includes full access for 7 days. There's nothing Boost adds that the trial doesn't cover — save it for later.",
              },
            ].map((faq, i) => (
              <div key={i} className="py-5">
                <p className="text-[14px] font-semibold text-[#111] mb-1.5">{faq.q}</p>
                <p className="text-[13px] text-[#6B7280] leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-y border-[#E5E5E0] bg-white">
        <div className="mx-auto max-w-[1200px] px-5 py-5 flex items-center justify-center gap-10 flex-wrap text-[13px] text-[#6B7280]">
          <span className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-[#0F766E]" /> Private and Encrypted</span>
          <span className="flex items-center gap-1.5"><Download className="h-3.5 w-3.5 text-[#0F766E]" /> Export your data anytime</span>
          <span className="flex items-center gap-1.5"><ShieldOff className="h-3.5 w-3.5 text-[#0F766E]" /> No ads. No data selling.</span>
          <span className="flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-[#0F766E]" /> Recall alerts free forever</span>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#111]">
        <div className="mx-auto max-w-[1200px] px-5 py-20 text-center">
          <h2 className="font-editorial text-[clamp(1.8rem,3vw,2.8rem)] text-white leading-[1.1] mb-3">
            You've been meaning to do this for years.<br />Get started in minutes.
          </h2>
          <p className="text-[14px] text-white/55 mb-8">
            Future you will be unreasonably smug about it.
          </p>
          <Link to="/login" className="inline-block rounded-[10px] bg-white px-6 py-3 text-sm font-bold text-[#111] hover:bg-white/90 transition-colors">
            Let's get this over with
          </Link>
          <p className="mt-3 text-[12px] text-white/35">Free to start. Painless to use. Mildly life-changing.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E5E5E0] bg-[#FAFAF7]">
        <div className="mx-auto max-w-[1200px] px-5 py-7 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-md overflow-hidden">
              <img src={manifestLogo} alt="Manifest" className="h-full w-full object-contain" loading="lazy" decoding="async" />
            </div>
            <span className="font-editorial text-xl">Manifest</span>
          </div>
          <div className="flex items-center gap-4 text-[13px] text-[#6B7280]">
            <Link to="/blog" className="hover:text-[#111] transition-colors">Blog</Link>
            <Link to="/privacy" className="hover:text-[#111] transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-[#111] transition-colors">Terms</Link>
            <Link to="/login" className="hover:text-[#111] transition-colors">Sign in</Link>
          </div>
          <span className="text-[13px] text-[#9CA3AF]">© 2026 Manifest</span>
        </div>
      </footer>
    </div>
  );
}
